import i18n from '@/utils/i18n';
import { ApolloError } from '@apollo/client';
import { formatCurrency } from '@/utils/money';
import { GetPeriodSalesAnalyticsResponse } from '@/types';
import { useGeneratedSalesPortalAccountDetailMetricsQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/documents/sales-portal-account-detail-metrics.generated';
import { useMemo, useState } from 'react';

export enum MetricsTimeRange {
  MTD = 'MTD',
  WTD = 'WTD',
  YTD = 'YTD',
}

const DEFAULT_TIME_RANGE = MetricsTimeRange.MTD;

export interface AccountDetailMetric {
  changePercentage: number | null;
  label: string;
  value: string;
}

interface AccountDetailMetricsLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
}

interface AccountDetailMetricsLoaderResult {
  error?: ApolloError;
  metrics: AccountDetailMetric[] | null;
  onTimeRangeChange: (timeRange: MetricsTimeRange) => void;
  selectedTimeRange: MetricsTimeRange;
}

/**
 * Loader hook for fetching key performance metrics for the Account Detail screen.
 * Fetches all periods (WTD/MTD/YTD) in a single query and selects the active
 * period based on user selection. Defaults to MTD.
 *
 * @param params.restaurantUUID - The UUID of the restaurant
 * @param params.supplierUUID - The UUID of the supplier
 * @returns Formatted metrics for the selected period, the query error, and time range controls
 */
export const useAccountDetailMetricsLoader = ({
  restaurantUUID,
  supplierUUID,
}: AccountDetailMetricsLoaderParams): AccountDetailMetricsLoaderResult => {
  const [selectedTimeRange, setSelectedTimeRange] =
    useState<MetricsTimeRange>(DEFAULT_TIME_RANGE);

  const { data, error } = useGeneratedSalesPortalAccountDetailMetricsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const metrics = useMemo<AccountDetailMetric[] | null>(() => {
    const allPeriods: GetPeriodSalesAnalyticsResponse[] | undefined =
      data?.periodSalesAnalytics;
    if (!allPeriods || error) {
      return null;
    }

    const activePeriod = allPeriods.find(
      (period: GetPeriodSalesAnalyticsResponse) =>
        period.period_type.toLowerCase() === selectedTimeRange.toLowerCase(),
    );
    if (!activePeriod) {
      return [];
    }

    return [
      {
        changePercentage: activePeriod.total_orders_percent_change ?? null,
        label: i18n.t('sales_portal.account_detail.metrics.total_orders'),
        value: activePeriod.total_orders.toLocaleString(),
      },
      {
        changePercentage: activePeriod.total_sales_percent_change ?? null,
        label: i18n.t('sales_portal.account_detail.metrics.total_sales'),
        value: formatCurrency(
          activePeriod.total_sales_in_micros,
          activePeriod.currency_code,
        ),
      },
      {
        changePercentage: activePeriod.avg_order_size_percent_change ?? null,
        label: i18n.t('sales_portal.account_detail.metrics.avg_order_size'),
        value: i18n.t('sales_portal.account_detail.metrics.units', {
          count: activePeriod.avg_order_size.toLocaleString(),
        }),
      },
    ];
  }, [data, error, selectedTimeRange]);

  return {
    error,
    metrics,
    onTimeRangeChange: setSelectedTimeRange,
    selectedTimeRange,
  };
};
